import { computed, inject, Injectable } from '@angular/core';
import { LEERPLUS } from '../rid-game.utils';

const DAY_MS = 24 * 60 * 60 * 1000;

@Injectable()
export class RidCalendarService {
  private readonly $lpApiService = inject(LEERPLUS.apiToken);

  /** The date the game plays on (YYYY-MM-DD): the date stored in the save for debugging, otherwise today. */
  public readonly today = computed(() => this.$lpApiService.state().date ?? toDateString(new Date()));

  /** The homework's week is the week of the real today, so a date in a later week moves the week along with it. */
  public readonly currentWeekIndex = computed(
    () => this.$lpApiService.state().homework.weekIndex + weeksBetween(toDateString(new Date()), this.today()),
  );

  /** The day of the week (0-based, Monday is 0) the game plays on. */
  public readonly currentDayOfWeek = computed(() => dayOfWeek(fromDateString(this.today())));

  /** The days (0-based) since the Monday of the first homework week. */
  public readonly currentDayIndex = computed(() => this.currentWeekIndex() * 7 + this.currentDayOfWeek());

  public nextDay() {
    const date = fromDateString(this.today());
    date.setDate(date.getDate() + 1);
    this.setDate(toDateString(date));
  }

  public resetDate() {
    this.setDate(null);
  }

  private setDate(date: string | null) {
    this.$lpApiService.state.update((save) => ({ ...save, date }));
  }
}

// dates are local calendar days; toISOString() would give the UTC day, which is yesterday shortly after midnight in the Netherlands
function toDateString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function fromDateString(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** The number of weeks (starting on Monday) from the week of `from` to the week of `to`. */
function weeksBetween(from: string, to: string): number {
  return Math.round((startOfWeek(fromDateString(to)) - startOfWeek(fromDateString(from))) / (7 * DAY_MS));
}

function startOfWeek(date: Date): number {
  const monday = new Date(date);
  monday.setDate(date.getDate() - dayOfWeek(date));
  return monday.getTime();
}

function dayOfWeek(date: Date): number {
  return (date.getDay() + 6) % 7;
}
